import { Injectable } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames } from '@office-start/telemetry-types';
import { filter, take } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { ObservableScope } from '../models/observable-scope.enum';
import { ConfigParserService } from '../services/config-parser.service';
import { ConsumerLicenseService, ConsumerSkuInfo, ConsumerSkuType } from '../services/consumer-license.service';
import { EventService } from '../services/event.service';
import { DOMInteractiveMarker } from '../utilities/dom-interactive-marker.utility';
import { AngularEvents } from './angular-events';
import { CapabilitiesService } from './capabilities.service';
import { AuthType, ClientStartupConfigService } from './client-startup-config.service';

export interface ConsumerInstallConfig {
    currentSkuInfo: ConsumerSkuInfo;
    expiredSkuInfo: ConsumerSkuInfo;
}

@Injectable({
    providedIn: 'root'
})
export class ConsumerInstallConfigService {
    public static readonly consumerInstallConfigElementId = 'consumerInstallInfo';

    private configPromise: Promise<ConsumerInstallConfig>;
    private refreshedConfig: Promise<ConsumerInstallConfig>;

    constructor(
        private readonly consumerLicenseService: ConsumerLicenseService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly configParserService: ConfigParserService,
        private readonly eventService: EventService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly instrumentationService: InstrumentationService,
        private readonly startupConfigService: ClientStartupConfigService
    ) {
        this.eventService.events.withScope(ObservableScope.OnlyFuture)
        .pipe(filter(event => event.name === AngularEvents.convertedToPaid), take(1))
        .toPromise()
        .then(() => {
            this.refreshedConfig = this.configPromise.then(config => {
                if (config) {
                    return {
                        currentSkuInfo: { skuType: ConsumerSkuType.O365Personal, daysExpired: -365 },
                        expiredSkuInfo: config.expiredSkuInfo
                    };
                }

                return {
                    currentSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 },
                    expiredSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 }
                };
            });
        });
    }

    // It is possible we don't have the 3rd chunk with consumer info and tokens yet, so we may need to call this in the code where we use config
    public getConfig(): Promise<ConsumerInstallConfig> {
        if (this.refreshedConfig) {
            return this.refreshedConfig;
        }

        if (!this.configPromise) {
            if (this.startupConfigService.config.authType !== AuthType.MSA) {
                return Promise.resolve({
                    currentSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 },
                    expiredSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 }
                });
            }

            if (this.capabilitiesService.capabilities.pwaEnabled) {
                this.configPromise = this.getPwaConfig();
            } else {
                const tokensLoadedPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                    filter(event => event.name === AngularEvents.primaryTokensLoaded),
                    take(1))
                    .toPromise();
                const domReadyPromise = DOMInteractiveMarker.fullPageReady();
                this.configPromise = Promise.race([tokensLoadedPromise, domReadyPromise]).then(() => this.configParserService.parse<ConsumerInstallConfig>(ConsumerInstallConfigService.consumerInstallConfigElementId));
            }
        }
        return this.configPromise;
    }

    public getCurrentSku(): Promise<ConsumerSkuType> {
        return new Promise<ConsumerSkuType>( resolve => {
            this.getConfig()
            .then( config => resolve(config && config.currentSkuInfo ? config.currentSkuInfo.skuType : ConsumerSkuType.NotDetected))
            .catch(() => resolve( ConsumerSkuType.NotDetected));
        });
    }

    private getPwaConfig(): Promise<ConsumerInstallConfig> {
        return new Promise<ConsumerInstallConfig>(resolve => {
            try {
                const licensesStream = this.consumerLicenseService.getLicenses();
                licensesStream.results.subscribe(
                    licenses => {
                        const params: InstrumentationParams = {};
                        params[InstrumentationParam.State] = 'Succeeded';
                        params[InstrumentationParam.DebugInfo] = 'ProductIds: ' + licenses.diagnosticInfo;
                        params[InstrumentationParam.SkuType] = ConsumerSkuType[licenses.currentSkuInfo.skuType];
                        params[InstrumentationParam.ActiveDaysLeft] = (-licenses.currentSkuInfo.daysExpired).toString();
                        params[InstrumentationParam.ExpiredSkuType] = ConsumerSkuType[licenses.expiredSkuInfo.skuType];
                        params[InstrumentationParam.DaysExpired] = licenses.expiredSkuInfo.daysExpired.toString();
                        params[InstrumentationParam.FromServiceWorkerCache] = (licenses.fromCache).toString();
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.BulkLicensePreview, params);
                        logFeature({
                            featureName: KnownFeatureNames.Upsell,
                            eventName: KnownInstrumentationEvents.BulkLicensePreview,
                            area: 'Other',
                            params: {
                                ElementId: 'bulk-license-preview-api',
                                State: 'Succeeded',
                                DebugInfo: `ProductIds: ${licenses.diagnosticInfo}`,
                                SkuType: ConsumerSkuType[licenses.currentSkuInfo.skuType],
                                ActiveDaysLeft: licenses.currentSkuInfo.daysExpired,
                                ExpiredSkuType: ConsumerSkuType[licenses.expiredSkuInfo.skuType],
                                DaysExpired: licenses.expiredSkuInfo.daysExpired,
                                FromServiceWorkerCache: (licenses.fromCache).toString()
                            }
                        });
                        resolve({
                            currentSkuInfo: licenses.currentSkuInfo,
                            expiredSkuInfo: licenses.expiredSkuInfo
                        });
                    }
                );

                licensesStream.initialCompletion.then(error => {
                    if (!error) {
                        return;
                    }

                    if (!(error.cacheError && error.networkError)) {
                        this.globalErrorHandler.handleCacheObservableError(error, ErrorId.xJ743, ErrorId.QhHdV);
                        return;
                    }

                    //Both network and cache request failed. Error details in Unhandled_Exception.
                    this.logFailureEvent();
                    this.globalErrorHandler.handleError(error, ErrorId.IMo7P);
                    resolve({
                        currentSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 },
                        expiredSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 }
                    });
                });
            } catch (error) {
                this.logFailureEvent();
                    resolve({
                        currentSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 },
                        expiredSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 }
                    });
            }
        });
    }

    private logFailureEvent() {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.State] = 'Failed';
        params[InstrumentationParam.SkuType] = ConsumerSkuType[ConsumerSkuType.NotDetected];
        params[InstrumentationParam.ActiveDaysLeft] = '-1';
        params[InstrumentationParam.ExpiredSkuType] = ConsumerSkuType[ConsumerSkuType.NotDetected];
        params[InstrumentationParam.DaysExpired] = '-1';
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.BulkLicensePreview, params);
        logFeature({
            featureName: KnownFeatureNames.Upsell,
            eventName: KnownInstrumentationEvents.BulkLicensePreview,
            area: 'Other',
            params: {
                ElementId: 'bulk-license-preview-api',
                State: 'Failed',
                SkuType: ConsumerSkuType[ConsumerSkuType.NotDetected],
                ActiveDaysLeft: -1,
                ExpiredSkuType: ConsumerSkuType[ConsumerSkuType.NotDetected],
                DaysExpired: -1
            }
        });
    }
}
